import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend 
} from 'recharts';
import { 
  TrendingUp, Package, FlaskConical, AlertTriangle, CheckCircle2, 
  BarChart2, Plus, Pencil, Trash2, ClipboardList, FileDown, Search, Clipboard,
  Beaker, Droplets, Truck, ShieldCheck, Clock, Calendar, CheckSquare
} from 'lucide-react';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import SearchableSelect from '@/components/ui/SearchableSelect';
import ProgressRing from '@/components/production/ProgressRing';
import { exportToPDF } from '@/utils/exportPDF';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const STATUS_COLORS = { pending: '#94a3b8', in_progress: '#f59e0b', filling: '#3b82f6', qc: '#8b5cf6', packing: '#f97316', done: '#22c55e' };
const PIE_COLORS = ['#22c55e', '#f59e0b', '#94a3b8', '#3b82f6', '#8b5cf6', '#f97316'];

const ACTIVITIES = [
  { value: 'racik', label: 'Mixing', color: 'bg-purple-50 text-purple-700 border-purple-200/50', icon: Beaker },
  { value: 'filling', label: 'Filling', color: 'bg-cyan-50 text-cyan-700 border-cyan-200/50', icon: Droplets },
  { value: 'packing', label: 'Pengemasan', color: 'bg-orange-50 text-orange-700 border-orange-200/50', icon: Package },
];

const SHIFT_OPTIONS = [
  { value: 'pagi', label: 'Pagi' },
  { value: 'siang', label: 'Siang' },
  { value: 'malam', label: 'Malam' },
];

function ActivityBadge({ activity }) {
  const act = ACTIVITIES.find(x => x.value === activity) || ACTIVITIES[ACTIVITIES.length - 1];
  const Icon = act.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${act.color}`}>
      <Icon className="w-3 h-3" />
      {act.label}
    </span>
  );
}

const formatDuration = (val) => {
  if (val === undefined || val === null) return "0 menit";
  let mins = val;
  if (typeof val === 'string') {
    // Extract only numbers from the string
    const match = val.match(/\d+/);
    mins = match ? parseInt(match[0], 10) : 0;
  }
  if (isNaN(mins) || mins <= 0) return "0 menit";
  const h = Math.floor(mins / 60);
  const m = Math.floor(mins % 60);
  if (h > 0) return `${h} jam${m > 0 ? ` ${m} menit` : ''}`;
  return `${m} menit`;
};

function EntryDialog({ entry, orders, onSave, trigger }) {
  const isEdit = !!entry;
  const today = new Date().toISOString().split('T')[0];
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(
    entry
      ? { date: entry.date, brand_name: entry.brand_name, activity: entry.activity, qty_produced: entry.qty_produced, operator: entry.operator || '', shift: entry.shift || 'pagi', notes: entry.notes || '', waste_qty: entry.waste_qty || 0, target_po: entry.target_po || '', duration_minutes: entry.duration_minutes || '' }
      : { date: today, brand_name: '', activity: 'filling', qty_produced: '', operator: '', shift: 'pagi', notes: '', waste_qty: 0, target_po: '', duration_minutes: '' }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave({ 
      ...form, 
      qty_produced: Number(form.qty_produced) || 0, 
      waste_qty: Number(form.waste_qty) || 0,
      duration_minutes: Number(form.duration_minutes) || 0
    });
    setOpen(false);
  };

  const brandOptions = [...new Set(orders.map(o => o.brand_name).filter(Boolean))].map(b => ({ value: b, label: b }));
  const poOptions = orders.map(o => ({ value: o.order_number || o.id, label: `${o.brand_name} - ${o.product_name} (${o.order_number || 'PO'})` }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md bg-[#FFFDF9] border border-[#E6D5B8]/80 rounded-2xl shadow-xl">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-xl text-[#302621]">{isEdit ? 'Edit Catatan Produksi' : 'Tambah Catatan Produksi'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#7A695B]">Tanggal</Label>
              <CustomDatePicker value={form.date} onChange={(val) => setForm(f => ({...f, date: val}))} required className="h-9 border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]" />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#7A695B]">Shift</Label>
              <SearchableSelect value={form.shift} onValueChange={v => setForm(f => ({ ...f, shift: v }))} options={SHIFT_OPTIONS} />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#7A695B]">Mitra / Brand</Label>
            {brandOptions.length > 0 ? (
              <SearchableSelect
                value={form.brand_name}
                onValueChange={v => setForm(f => ({ ...f, brand_name: v }))}
                options={brandOptions}
                placeholder="Pilih brand..."
              />
            ) : (
              <Input value={form.brand_name} onChange={(val) => setForm(f => ({...f, brand_name: val}))} placeholder="Nama brand" required className="h-9 border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]" />
            )}
          </div>
          <div>
            <Label className="text-xs font-bold text-[#7A695B]">Target PO</Label>
            <SearchableSelect
              value={form.target_po}
              onValueChange={v => setForm(f => ({ ...f, target_po: v }))}
              options={poOptions}
              placeholder="Hubungkan ke PO..."
            />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#7A695B]">Aktivitas Produksi</Label>
            <SearchableSelect 
              value={form.activity} 
              onValueChange={v => setForm(f => ({ ...f, activity: v }))} 
              options={ACTIVITIES.map(a => ({ value: a.value, label: a.label }))} 
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#7A695B]">Hasil Produksi (pcs)</Label>
              <Input type="number" value={form.qty_produced} onChange={(val) => setForm(f => ({...f, qty_produced: val}))} required className="h-9 border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]" />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#7A695B]">Waste / Reject (pcs)</Label>
              <Input type="number" value={form.waste_qty} onChange={(val) => setForm(f => ({...f, waste_qty: val}))} className="h-9 border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#7A695B]">Operator</Label>
              <Input value={form.operator} onChange={(val) => setForm(f => ({...f, operator: val}))} placeholder="Nama operator" className="h-9 border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]" />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#7A695B]">Durasi (menit)</Label>
              <Input type="number" value={form.duration_minutes} onChange={(val) => setForm(f => ({...f, duration_minutes: val}))} placeholder="15" className="h-9 border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#7A695B]">Catatan / Kendala</Label>
            <Textarea value={form.notes} onChange={(val) => setForm(f => ({...f, notes: val}))} placeholder="Tulis kendala mesin, reject bahan..." className="min-h-[80px] border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]" />
          </div>
          <Button type="submit" className="w-full bg-[#302621] text-white hover:bg-[#4A3B32] rounded-xl">{isEdit ? 'Simpan Catatan' : 'Catat Output'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


export default function Reporting() {
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState('30');
  const [activeReportTab, setActiveReportTab] = useState('catatan_harian');
  // Logs filters
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRangeType, setDateRangeType] = useState('hari_ini');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [filterShift, setFilterShift] = useState('all');
  const [filterActivity, setFilterActivity] = useState('all');

  const [qcModalData, setQcModalData] = useState(null);

  const formatFullDateTime = (dateVal) => {
    if (!dateVal) return 'Belum diatur';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return 'Belum diatur';
      const day = d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
      const time = d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }).replace('.', ':');
      return `${day}, ${time} WIB`;
    } catch (e) {
      return 'Belum diatur';
    }
  };

  const formatDateOnlyStr = (dateVal) => {
    if (!dateVal) return 'Belum diatur';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return 'Belum diatur';
      return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
      return 'Belum diatur';
    }
  };

  const { data: orders = [] } = useQuery({
    queryKey: ['production-orders'],
    queryFn: () => base44.entities.ProductionOrder.list('order_number'),
  });

  const { data: clientPos = [] } = useQuery({
    queryKey: ['client-pos-reporting'],
    queryFn: () => base44.entities.ClientPO.list(),
  });

  const { data: logs = [], isLoading: isLoadingLogs } = useQuery({
    queryKey: ['daily-production'],
    queryFn: () => base44.entities.DailyProduction.list('-date'),
  });

  const { data: productionTasks = [] } = useQuery({
    queryKey: ['production-tasks'],
    queryFn: () => base44.entities.ProductionTask.list(),
  });

  const { data: tests = [] } = useQuery({
    queryKey: ['qc-tests'],
    queryFn: () => base44.entities.QCTest.list('-test_date'),
  });

  const { data: inventory = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => base44.entities.InventoryItem.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.DailyProduction.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['daily-production'] }); toast.success('Catatan harian dicatat'); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.DailyProduction.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['daily-production'] }); toast.success('Catatan harian diperbarui'); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.DailyProduction.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['daily-production'] }); toast.success('Catatan harian dihapus'); },
  });

  // Combine live tasks with completed logs
  const combinedLogs = React.useMemo(() => {
    const liveTasks = productionTasks
      .filter(t => t.status === "in_progress" || t.status === "on_hold")
      .map(t => {
        const today = new Date().toISOString().split('T')[0];
        
        let dur = 0;
        if (t.start_time) {
           dur = Math.round((new Date().getTime() - new Date(t.start_time).getTime()) / 60000);
           if (t.total_pause_minutes) dur -= t.total_pause_minutes;
        }

        const actMap = { racik: "mixing", maserasi: "lainnya", filling: "filling", packing: "packing", labeling: "packing", qc: "quality_check", preparation: "lainnya", other: "lainnya" };

        return {
          id: `live-${t.id}`,
          isLive: true,
          liveStatus: t.status,
          date: today,
          brand_name: t.po_brand || "Tugas Produksi",
          target_po: t.po_number || "",
          activity: actMap[t.activity_type] || "lainnya",
          qty_produced: 0,
          target_quantity: t.target_quantity || 0,
          waste_qty: 0,
          operator: "Tim Produksi (Live)",
          duration_minutes: dur > 0 ? dur : 0,
          notes: t.task_name,
          shift: "Aktif",
          _rawTask: t
        };
      });

    return [...liveTasks, ...logs];
  }, [logs, productionTasks]);

  // 1. FILTER CATATAN HARIAN
  const getFilterDateRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const y = today.getFullYear();
    const m = today.getMonth();
    const d = today.getDate();

    let start = null;
    let end = new Date(today);

    if (dateRangeType === 'hari_ini') {
      start = new Date(today);
    } else if (dateRangeType === 'kemarin') {
      start = new Date(today);
      start.setDate(d - 1);
      end = new Date(start);
    } else if (dateRangeType === '7_hari') {
      start = new Date(today);
      start.setDate(d - 6);
    } else if (dateRangeType === '30_hari') {
      start = new Date(today);
      start.setDate(d - 29);
    } else if (dateRangeType === 'sebulan') {
      start = new Date(y, m, 1);
      end = new Date(y, m + 1, 0);
    } else if (dateRangeType === 'custom') {
      start = customStartDate ? new Date(customStartDate) : null;
      end = customEndDate ? new Date(customEndDate) : null;
    } else if (dateRangeType === 'all_time') {
      start = null;
      end = null;
    }

    return { 
      startStr: start ? start.toISOString().split('T')[0] : null, 
      endStr: end ? end.toISOString().split('T')[0] : null 
    };
  };

  const filteredLogs = combinedLogs.filter(log => {
    const q = searchTerm.toLowerCase();
    const matchSearch = !q || 
      log.brand_name?.toLowerCase().includes(q) || 
      log.operator?.toLowerCase().includes(q) || 
      log.notes?.toLowerCase().includes(q) ||
      log.target_po?.toLowerCase().includes(q);
      
    const { startStr, endStr } = getFilterDateRange();
    let matchDate = true;
    if (startStr && log.date < startStr) matchDate = false;
    if (endStr && log.date > endStr) matchDate = false;

    const matchShift = filterShift === 'all' || log.shift === filterShift;
    const matchActivity = filterActivity === 'all' || log.activity === filterActivity;

    return matchSearch && matchDate && matchShift && matchActivity;
  });

  const groupedLogsUnsorted = React.useMemo(() => {
    if (filterActivity !== 'all') return [];
    const groups = {};
    filteredLogs.forEach(log => {
      const key = log.target_po || log.brand_name || 'Tanpa PO/Brand';
      if (!groups[key]) {
        groups[key] = {
          id: key,
          brand_name: log.brand_name,
          target_po: log.target_po,
          mixingDuration: 0,
          fillingDuration: 0,
          packingDuration: 0,
          mixingPause: 0,
          fillingPause: 0,
          packingPause: 0,
          mixingNotes: [],
          fillingNotes: [],
          packingNotes: [],
          mixingStartTime: null,
          mixingEndTime: null,
          fillingStartTime: null,
          fillingEndTime: null,
          packingStartTime: null,
          packingEndTime: null,
          totalDuration: 0,
          totalPause: 0,
          totalWaste: 0,
          mixingOutput: 0,
          fillingOutput: 0,
          packingOutput: 0,
          latestDate: '1970-01-01',
          items: []
        };
      }
      const g = groups[key];
      let pause = log._rawTask?.total_pause_minutes || 0;
      let actualNote = log.notes;

      if (log.notes) {
        if (!pause) {
          const match = log.notes.match(/Jeda:\s*(\d+)m/i);
          if (match) pause = parseInt(match[1], 10);
        }
        
        if (log.notes.startsWith('[Tugas]')) {
          const noteMatch = log.notes.match(/Bersih:\s*\d+m\.?\s*(.*)/i);
          if (noteMatch && noteMatch[1].trim()) {
            actualNote = noteMatch[1].trim();
          } else {
            actualNote = ''; // No user note attached
          }
        }
      }
      
      g.items.push(log);
      g.totalDuration += (log.duration_minutes || 0);
      g.totalPause += pause;
      g.totalWaste += (log.waste_qty || 0);

      if (log.date && log.date > g.latestDate) {
        g.latestDate = log.date;
      }

      const durMinutes = Number(log.duration_minutes || log._rawTask?.duration_minutes || 0);
      const totalElapsedMinutes = durMinutes + pause;

      // Start timestamp
      let startTs = log._rawTask?.started_at || log._rawTask?.start_time || log.created_at || log.created_date || (log.date ? `${log.date}T08:00:00` : null);

      // End timestamp
      let endTs = log.completed_at || log._rawTask?.completed_at || log.updated_at;

      // If endTs is missing or equals startTs, but we have totalElapsedMinutes > 0, calculate endTs = startTs + totalElapsedMinutes
      if (startTs && (!endTs || endTs === startTs) && totalElapsedMinutes > 0) {
        try {
          const sDate = new Date(startTs);
          if (!isNaN(sDate.getTime())) {
            endTs = new Date(sDate.getTime() + totalElapsedMinutes * 60000).toISOString();
          }
        } catch (e) {}
      }

      // Conversely, if startTs is missing, calculate startTs = endTs - totalElapsedMinutes
      if (!startTs && endTs && totalElapsedMinutes > 0) {
        try {
          const eDate = new Date(endTs);
          if (!isNaN(eDate.getTime())) {
            startTs = new Date(eDate.getTime() - totalElapsedMinutes * 60000).toISOString();
          }
        } catch (e) {}
      }

      if (log.activity === 'mixing') {
        g.mixingDuration += (log.duration_minutes || 0);
        g.mixingOutput += (Number(log.qty_produced) || 0);
        g.mixingPause += pause;
        if (actualNote) g.mixingNotes.push(actualNote);
        if (startTs && (!g.mixingStartTime || new Date(startTs) < new Date(g.mixingStartTime))) g.mixingStartTime = startTs;
        if (endTs && (!g.mixingEndTime || new Date(endTs) > new Date(g.mixingEndTime))) g.mixingEndTime = endTs;
      } else if (log.activity === 'filling') {
        g.fillingDuration += (log.duration_minutes || 0);
        g.fillingOutput += (Number(log.qty_produced) || 0);
        g.fillingPause += pause;
        if (actualNote) g.fillingNotes.push(actualNote);
        if (startTs && (!g.fillingStartTime || new Date(startTs) < new Date(g.fillingStartTime))) g.fillingStartTime = startTs;
        if (endTs && (!g.fillingEndTime || new Date(endTs) > new Date(g.fillingEndTime))) g.fillingEndTime = endTs;
      } else if (log.activity === 'packing' || log.activity === 'packaging' || log.activity === 'pengemasan') {
        g.packingDuration += (log.duration_minutes || 0);
        g.packingOutput += (Number(log.qty_produced) || 0);
        g.packingPause += pause;
        if (actualNote) g.packingNotes.push(actualNote);
        if (startTs && (!g.packingStartTime || new Date(startTs) < new Date(g.packingStartTime))) g.packingStartTime = startTs;
        if (endTs && (!g.packingEndTime || new Date(endTs) > new Date(g.packingEndTime))) g.packingEndTime = endTs;
      }
    });
    return Object.values(groups);
  }, [filteredLogs, filterActivity]);

  const [sortGroupBy, setSortGroupBy] = useState('newest');

  const groupedLogs = React.useMemo(() => {
    const arr = [...groupedLogsUnsorted];
    if (sortGroupBy === 'alphabetical') {
      arr.sort((a, b) => (a.brand_name || '').localeCompare(b.brand_name || ''));
    } else {
      arr.sort((a, b) => b.latestDate.localeCompare(a.latestDate));
    }
    return arr;
  }, [groupedLogsUnsorted, sortGroupBy]);

  // Batch selection hook — only for non-live logs
  const deletableFilteredLogs = React.useMemo(() => {
    return filteredLogs.filter(l => !l.isLive);
  }, [filteredLogs]);

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount,
    totalCount: batchTotalCount
  } = useBatchSelection(deletableFilteredLogs, l => l.id);

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await base44.entities.DailyProduction.delete(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-production'] });
      clearSelection();
      toast.success('Catatan terpilih berhasil dihapus');
    },
  });

  // Calculate filtered totals
  const totalLogsCount = combinedLogs.filter(l => !l.isLive).length;
  const todayStr = new Date().toISOString().split('T')[0];
  const logsTodayCount = combinedLogs.filter(l => !l.isLive && l.date === todayStr).length;
  const totalQtyProduced = filterActivity === 'all' 
    ? groupedLogs.reduce((sum, g) => sum + Math.max(g.fillingOutput, g.packingOutput, g.mixingOutput), 0)
    : filteredLogs.reduce((sum, l) => sum + (l.qty_produced || 0), 0);
  const totalWasteProduced = filterActivity === 'all'
    ? groupedLogs.reduce((sum, g) => sum + g.totalWaste, 0)
    : filteredLogs.reduce((sum, l) => sum + (l.waste_qty || 0), 0);

  // PDF Export
  const handleExportPDF = () => {
    exportToPDF({
      title: 'Laporan Catatan Produksi Harian',
      subtitle: `Tanggal Cetak: ${new Date().toLocaleDateString('id-ID')} · Total ${totalLogsCount} catatan`,
      filename: `Laporan-Produksi-Satala-${new Date().toISOString().split('T')[0]}`,
      columns: [
        { key: 'date', label: 'Tanggal' },
        { key: 'brand_name', label: 'Brand' },
        { key: 'target_po', label: 'Nomor PO' },
        { key: 'activity', label: 'Aktivitas', render: r => (ACTIVITIES.find(a => a.value === r.activity) || { label: r.activity }).label },
        { key: 'qty_produced', label: 'Output (pcs)', render: r => (r.qty_produced || 0).toLocaleString() },
        { key: 'waste_qty', label: 'Waste (pcs)', render: r => r.waste_qty || 0 },
        { key: 'operator', label: 'Operator', render: r => r.operator || '-' },
        { key: 'duration_minutes', label: 'Durasi (menit)', render: r => r.duration_minutes || '-' }
      ],
      rows: filteredLogs
    });
  };

  // 2. ANALYTICS PREPARATION (Rekap & Analitik)
  const since = new Date();
  since.setDate(since.getDate() - Number(period));
  const recentLogs = logs.filter(l => l.date && new Date(l.date) >= since);

  const totalPO = orders.length;
  const donePO = orders.filter(o => o.status === 'done').length;
  const avgProgress = orders.length > 0
    ? Math.round(orders.reduce((sum, o) => {
        const comps = o.components || [];
        const p = comps.length > 0 ? comps.filter(c => c.status === 'ready').length / comps.length * 100 : 0;
        return sum + p;
      }, 0) / orders.length)
    : 0;

  const wasteRate = totalQtyProduced > 0 ? ((totalWasteProduced / totalQtyProduced) * 100).toFixed(1) : 0;
  const criticalItems = inventory.filter(i => (i.total_stock - (i.allocated_qty || 0)) <= 0).length;

  const last14 = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (13 - i));
    const dateStr = d.toISOString().split('T')[0];
    const dayLogs = logs.filter(l => l.date === dateStr);
    return {
      label: d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }),
      qty: dayLogs.reduce((s, l) => s + (l.qty_produced || 0), 0),
      waste: dayLogs.reduce((s, l) => s + (l.waste_qty || 0), 0),
    };
  });

  const activityMap = {};
  recentLogs.forEach(l => {
    activityMap[l.activity] = (activityMap[l.activity] || 0) + (l.qty_produced || 0);
  });
  const activityData = Object.entries(activityMap).map(([name, value]) => ({ name, value }));

  const statusMap = {};
  orders.forEach(o => { statusMap[o.status] = (statusMap[o.status] || 0) + 1; });
  const statusData = Object.entries(statusMap).map(([name, value]) => ({ name, value }));

  const brandMap = {};
  recentLogs.forEach(l => {
    brandMap[l.brand_name] = (brandMap[l.brand_name] || 0) + (l.qty_produced || 0);
  });
  const brandData = Object.entries(brandMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([name, qty]) => ({ name, qty }));

  const compReadiness = {};
  orders.forEach(o => {
    (o.components || []).forEach(c => {
      if (!compReadiness[c.name]) compReadiness[c.name] = { ready: 0, total: 0 };
      compReadiness[c.name].total++;
      if (c.status === 'ready') compReadiness[c.name].ready++;
    });
  });
  const compData = Object.entries(compReadiness).map(([name, { ready, total }]) => ({
    name, pct: Math.round((ready / total) * 100), ready, total,
  })).sort((a, b) => b.pct - a.pct);

  const qcByBrand = {};
  tests.forEach(t => {
    if (!qcByBrand[t.brand_name]) qcByBrand[t.brand_name] = { scores: [], acc: 0, total: 0 };
    if (t.score > 0) qcByBrand[t.brand_name].scores.push(t.score);
    qcByBrand[t.brand_name].total++;
    if (t.approval_status === 'acc') qcByBrand[t.brand_name].acc++;
  });
  const qcData = Object.entries(qcByBrand).map(([brand, d]) => ({
    brand,
    avgScore: d.scores.length > 0 ? (d.scores.reduce((a, b) => a + b, 0) / d.scores.length).toFixed(1) : '-',
    accRate: d.total > 0 ? Math.round((d.acc / d.total) * 100) : 0,
  }));

  // 3. MARGIN KEUNTUNGAN ANALYSIS
  const brandMarginsMap = {};
  
  orders.forEach(o => {
    if (o.brand_name && !brandMarginsMap[o.brand_name]) {
      brandMarginsMap[o.brand_name] = {
        brand: o.brand_name,
        qty_po: 0,
        qty_produced: 0,
        unit_price_avg: 45000, 
        unit_cost_avg: 28000,  
        factory_fee: 0
      };
    }
    if (o.brand_name) {
      brandMarginsMap[o.brand_name].qty_po += (o.qty || 1000);
      brandMarginsMap[o.brand_name].factory_fee += (Number(o.jasa_pabrik) || 4000) * (o.qty || 1000);
    }
  });

  logs.forEach(l => {
    if (l.brand_name) {
      if (!brandMarginsMap[l.brand_name]) {
        brandMarginsMap[l.brand_name] = {
          brand: l.brand_name,
          qty_po: 0,
          qty_produced: 0,
          unit_price_avg: 45000,
          unit_cost_avg: 28000,
          factory_fee: 0
        };
      }
      brandMarginsMap[l.brand_name].qty_produced += (l.qty_produced || 0);
    }
  });

  const marginsData = Object.values(brandMarginsMap).map(row => {
    const realQty = row.qty_produced || 1000;
    const omset = realQty * row.unit_price_avg;
    const modal = realQty * row.unit_cost_avg;
    const profit = omset - modal;
    const marginPct = omset > 0 ? ((profit / omset) * 100).toFixed(1) : 0;
    return {
      ...row,
      omset,
      modal,
      profit,
      marginPct
    };
  }).sort((a, b) => b.profit - a.profit);

  const totalFinancialOmset = marginsData.reduce((sum, r) => sum + r.omset, 0);
  const totalFinancialModal = marginsData.reduce((sum, r) => sum + r.modal, 0);
  const totalFinancialProfit = totalFinancialOmset - totalFinancialModal;
  const totalFinancialMarginPct = totalFinancialOmset > 0 ? ((totalFinancialProfit / totalFinancialOmset) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6 text-[#302621] font-sans pb-12">
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedCount}
        totalCount={deletableFilteredLogs.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={() => {
          if (confirm(`Yakin ingin menghapus ${selectedCount} catatan terpilih?`)) {
            batchDeleteMutation.mutate(Array.from(selectedIds));
          }
        }}
        isDeleting={batchDeleteMutation.isPending}
      />
      
      {/* HEADER LAPORAN */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Laporan & Analitik</h1>
          <p className="text-muted-foreground text-sm mt-1">Catatan harian & rekap keuntungan produksi terpadu</p>
        </div>
        <div className="flex items-center gap-2">
          {activeReportTab === 'rekap_analitik' && (
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40 border-[#E6D5B8]/80 bg-white">
                <BarChart2 className="w-4 h-4 mr-2 text-[#8C6208]" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 hari terakhir</SelectItem>
                <SelectItem value="14">14 hari terakhir</SelectItem>
                <SelectItem value="30">30 hari terakhir</SelectItem>
                <SelectItem value="90">3 bulan terakhir</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* THREE TABS NAVIGATOR */}
      <Tabs value={activeReportTab} onValueChange={setActiveReportTab} className="space-y-6">
        <TabsList className="grid grid-cols-2 h-auto p-1.5 rounded-2xl bg-white border border-[#E6D5B8]/60 shadow-xs max-w-lg">
          <TabsTrigger value="catatan_harian" className="py-2.5 rounded-xl font-bold text-xs gap-1.5 data-[state=active]:bg-[#302621] data-[state=active]:text-white">
            <Clipboard className="w-3.5 h-3.5" /> Catatan Harian
          </TabsTrigger>
          <TabsTrigger value="rekap_analitik" className="py-2.5 rounded-xl font-bold text-xs gap-1.5 data-[state=active]:bg-[#302621] data-[state=active]:text-white">
            <BarChart2 className="w-3.5 h-3.5" /> Rekap & Analitik
          </TabsTrigger>
        </TabsList>

        {/* ==================== TAB 1: CATATAN HARIAN ==================== */}
        <TabsContent value="catatan_harian" className="space-y-6 outline-none">
          {/* Card Counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#FFFDF9] rounded-2xl p-5 border border-[#E6D5B8]/60 shadow-xs">
              <span className="text-[10px] font-black text-[#A39171] uppercase tracking-wider block">Total Catatan</span>
              <div className="flex items-baseline gap-1 mt-1.5">
                <span className="text-3xl font-black text-[#302621]">{totalLogsCount}</span>
                <span className="text-[10px] text-[#7A695B] font-bold">semua waktu</span>
              </div>
            </div>
            <div className="bg-[#FFFDF9] rounded-2xl p-5 border border-[#E6D5B8]/60 shadow-xs">
              <span className="text-[10px] font-black text-[#A39171] uppercase tracking-wider block">Hari Ini</span>
              <div className="flex items-baseline gap-1 mt-1.5">
                <span className="text-3xl font-black text-amber-700">{logsTodayCount}</span>
                <span className="text-[10px] text-[#7A695B] font-bold">catatan hari ini</span>
              </div>
            </div>
            <div className="bg-[#FFFDF9] rounded-2xl p-5 border border-[#E6D5B8]/60 shadow-xs">
              <span className="text-[10px] font-black text-[#A39171] uppercase tracking-wider block">Output Terbesar</span>
              <div className="flex items-baseline gap-1 mt-1.5">
                <span className="text-3xl font-black text-emerald-700">{totalQtyProduced.toLocaleString()}</span>
                <span className="text-[10px] text-[#7A695B] font-bold">pcs/produk</span>
              </div>
            </div>
            <div className="bg-[#FFFDF9] rounded-2xl p-5 border border-[#E6D5B8]/60 shadow-xs">
              <span className="text-[10px] font-black text-[#A39171] uppercase tracking-wider block">Total Reject</span>
              <div className="flex items-baseline gap-1 mt-1.5">
                <span className="text-3xl font-black text-rose-600">{totalWasteProduced.toLocaleString()}</span>
                <span className="text-[10px] text-[#7A695B] font-bold">pcs terbuang</span>
              </div>
            </div>
          </div>

          {/* Filter Bar Controls */}
          <div className="bg-white border border-[#E6D5B8]/50 rounded-2xl p-5 space-y-4 shadow-xs">
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input 
                  placeholder="Cari produk, operat..." 
                  value={searchTerm} 
                  onChange={setSearchTerm} 
                  className="pl-9 h-10 border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]"
                />
              </div>
              <div className="w-[160px]">
                <Select value={dateRangeType} onValueChange={setDateRangeType}>
                  <SelectTrigger className="h-10 bg-white border-[#E6D5B8]/60 font-medium text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hari_ini">Hari Ini</SelectItem>
                    <SelectItem value="kemarin">Kemarin</SelectItem>
                    <SelectItem value="7_hari">7 Hari Terakhir</SelectItem>
                    <SelectItem value="30_hari">30 Hari Terakhir</SelectItem>
                    <SelectItem value="sebulan">Bulan Ini</SelectItem>
                    <SelectItem value="all_time">Semua Waktu</SelectItem>
                    <SelectItem value="custom">Custom Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {dateRangeType === 'custom' && (
                <>
                  <div className="w-[140px]">
                    <CustomDatePicker 
                      value={customStartDate} 
                      onChange={setCustomStartDate} 
                      className="h-10 border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]"
                    />
                  </div>
                  <div className="w-[140px]">
                    <CustomDatePicker 
                      value={customEndDate} 
                      onChange={setCustomEndDate} 
                      className="h-10 border-[#E6D5B8]/60 focus-visible:ring-[#8C6208]"
                    />
                  </div>
                </>
              )}
              <div className="w-[150px]">
                <SearchableSelect 
                  value={filterShift} 
                  onValueChange={setFilterShift} 
                  options={[
                    { value: 'all', label: 'Semua Shift' },
                    { value: 'pagi', label: 'Shift Pagi' },
                    { value: 'siang', label: 'Shift Siang' },
                    { value: 'malam', label: 'Shift Malam' }
                  ]}
                />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex justify-between items-center pt-2 border-t border-slate-100 flex-wrap gap-2">
              <Button 
                variant="outline" 
                onClick={handleExportPDF}
                className="gap-2 text-xs font-bold border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC] h-9 rounded-xl"
              >
                <FileDown className="w-4 h-4" /> Ekspor PDF
              </Button>
              <EntryDialog
                orders={orders}
                onSave={(data) => createMutation.mutateAsync(data)}
                trigger={
                  <Button className="gap-2 text-xs font-bold bg-[#302621] text-white hover:bg-[#4A3B32] h-9 rounded-xl">
                    <Plus className="w-4 h-4" /> Tambah Catatan
                  </Button>
                }
              />
            </div>
          </div>

          {/* Quick Activity Pill Filters */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 pt-1 scrollbar-hide">
            <button
              onClick={() => setFilterActivity('all')}
              className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all border ${
                filterActivity === 'all' 
                  ? 'bg-[#302621] text-white border-[#302621]' 
                  : 'bg-white text-[#7A695B] border-[#E6D5B8]/60 hover:bg-[#F9F5EC]'
              }`}
            >
              Semua Aktivitas
            </button>
            {ACTIVITIES.map(act => (
              <button
                key={act.value}
                onClick={() => setFilterActivity(act.value)}
                className={`shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all border flex items-center gap-1.5 ${
                  filterActivity === act.value 
                    ? 'bg-[#302621] text-white border-[#302621]' 
                    : 'bg-white text-[#7A695B] border-[#E6D5B8]/60 hover:bg-[#F9F5EC]'
                }`}
              >
                <act.icon className="w-3.5 h-3.5" />
                {act.label}
              </button>
            ))}
          </div>

          {/* Logs Timeline List */}
          {isLoadingLogs ? (
            <div className="space-y-3">{Array(3).fill(0).map((_, i) => <div key={i} className="h-20 rounded-2xl bg-muted animate-pulse" />)}</div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground bg-white rounded-2xl border border-[#E6D5B8]/40">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30 text-[#A39171]" />
              <p className="font-bold">Belum ada catatan produksi</p>
              <p className="text-xs mt-1">Coba sesuaikan filter pencarian atau tanggal Anda.</p>
            </div>
          ) : filterActivity === 'all' ? (
            <div className="space-y-4">
              <div className="flex justify-end mb-2">
                <Select value={sortGroupBy} onValueChange={setSortGroupBy}>
                  <SelectTrigger className="w-40 h-8 text-xs font-bold bg-white border-[#E6D5B8]/60">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Urut: Terbaru</SelectItem>
                    <SelectItem value="alphabetical">Urut: Abjad (A-Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {groupedLogs.map(group => {
                const matchingOrder = clientPos.find(o => 
                  (group.target_po && (o.invoice_number === group.target_po || o.no_po === group.target_po)) ||
                  (group.brand_name && (o.customerName === group.brand_name || o.nama_brand === group.brand_name))
                ) || orders.find(o => 
                  (group.target_po && (o.order_number === group.target_po || o.no_po === group.target_po)) ||
                  (group.brand_name && (o.brand_name === group.brand_name))
                );

                const deliveryDate = matchingOrder?.delivery_date || matchingOrder?.target_delivery_date || matchingOrder?.shipped_at || matchingOrder?.tanggal_kirim || matchingOrder?.estimated_delivery;
                const qcData = matchingOrder?.products?.find(p => p.qc_data)?.qc_data || matchingOrder?.qc_data;

                return (
                  <Card key={group.id} className="rounded-2xl border-[#E6D5B8]/40 shadow-sm overflow-hidden bg-white">
                    <div className="bg-[#F9F5EC] px-4 py-3 border-b border-[#E6D5B8]/40 flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-black text-lg text-[#302621]">{group.brand_name || 'Tanpa Brand'}</h3>
                          {group.target_po && <span className="text-xs font-mono font-bold bg-[#E6D5B8]/40 text-[#8C6208] px-2 py-0.5 rounded">PO: {group.target_po}</span>}
                        </div>
                        
                        <div className="flex items-center gap-3 text-xs text-slate-600 mt-1 flex-wrap">
                          <span className="flex items-center gap-1 font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200/60">
                            <Truck className="w-3.5 h-3.5 text-emerald-600" />
                            Estimasi Kirim: <strong className="text-emerald-900">{formatDateOnlyStr(deliveryDate)}</strong>
                          </span>

                          <button
                            type="button"
                            onClick={() => setQcModalData({ brand_name: group.brand_name, target_po: group.target_po, matchingOrder, qcData })}
                            className="flex items-center gap-1.5 font-bold text-[#8C6208] bg-white hover:bg-[#F9F5EC] px-2.5 py-0.5 rounded-lg border border-[#E6D5B8] shadow-2xs transition-colors"
                          >
                            <ShieldCheck className="w-3.5 h-3.5 text-[#8C6208]" />
                            <span>Lihat Checklist QC</span>
                          </button>
                        </div>
                      </div>

                      <div className="text-right">
                        <p className="text-xs text-slate-500 font-medium mb-1">
                          Total Waktu Keseluruhan: <span className="font-bold text-[#8C6208]">{formatDuration(group.totalDuration + group.totalPause)}</span>
                        </p>
                        <div className="flex gap-2 justify-end flex-wrap">
                          {group.totalWaste > 0 && <span className="text-[10px] text-rose-500 font-semibold bg-rose-50 px-1.5 py-0.5 rounded">Reject: {group.totalWaste}</span>}
                          {group.totalPause > 0 && (
                            <>
                              <span className="text-[10px] text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded">Jeda: {formatDuration(group.totalPause)}</span>
                              <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.5 rounded">Waktu Asli: {formatDuration(group.totalDuration)}</span>
                            </>
                          )}
                          {group.totalPause === 0 && (
                            <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.5 rounded">Waktu Asli: {formatDuration(group.totalDuration)}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {/* Mixing */}
                      <div className="bg-purple-50/50 rounded-xl p-3 border border-purple-100 relative space-y-2">
                        <div className="flex items-center gap-2 text-purple-700 font-bold text-sm">
                          <Beaker className="w-4 h-4" /> <span>Mixing</span>
                        </div>

                        <div className="text-[11px] text-slate-600 space-y-1 border-t border-purple-200/60 pt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Mulai:</span>
                            <strong className="text-slate-800 font-semibold">{formatFullDateTime(group.mixingStartTime)}</strong>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Selesai:</span>
                            <strong className="text-slate-800 font-semibold">{group.mixingEndTime ? formatFullDateTime(group.mixingEndTime) : 'Belum Selesai'}</strong>
                          </div>
                          {group.mixingPause > 0 ? (
                            <>
                              <div className="flex items-center justify-between">
                                <span className="text-amber-700 font-semibold">Waktu Jeda:</span>
                                <strong className="text-amber-800 font-bold bg-amber-100/70 px-1.5 py-0.5 rounded text-[10px]">{formatDuration(group.mixingPause)}</strong>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-slate-500">Waktu Asli (Bersih):</span>
                                <strong className="text-emerald-700 font-bold">{formatDuration(group.mixingDuration)}</strong>
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">Durasi pengerjaan:</span>
                              <strong className="text-purple-800 font-bold">{formatDuration(group.mixingDuration)}</strong>
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-0.5 font-bold">
                            <span className="text-slate-600">Output:</span>
                            <strong className="text-slate-900">{Math.round(group.mixingOutput || 0).toLocaleString('id-ID')} {group.latestDate < '2026-07-21' ? 'Pcs' : 'Liter'}</strong>
                          </div>
                        </div>

                        {group.mixingNotes.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-purple-200/50">
                            {group.mixingNotes.map((note, i) => <p key={i} className="text-[10px] text-purple-800 italic leading-snug mb-1">"{note}"</p>)}
                          </div>
                        )}
                      </div>

                      {/* Filling */}
                      <div className="bg-cyan-50/50 rounded-xl p-3 border border-cyan-100 relative space-y-2">
                        <div className="flex items-center gap-2 text-cyan-700 font-bold text-sm">
                          <Droplets className="w-4 h-4" /> <span>Filling</span>
                        </div>

                        <div className="text-[11px] text-slate-600 space-y-1 border-t border-cyan-200/60 pt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Mulai:</span>
                            <strong className="text-slate-800 font-semibold">{formatFullDateTime(group.fillingStartTime)}</strong>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Selesai:</span>
                            <strong className="text-slate-800 font-semibold">{group.fillingEndTime ? formatFullDateTime(group.fillingEndTime) : 'Belum Selesai'}</strong>
                          </div>
                          {group.fillingPause > 0 ? (
                            <>
                              <div className="flex items-center justify-between">
                                <span className="text-amber-700 font-semibold">Waktu Jeda:</span>
                                <strong className="text-amber-800 font-bold bg-amber-100/70 px-1.5 py-0.5 rounded text-[10px]">{formatDuration(group.fillingPause)}</strong>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-slate-500">Waktu Asli (Bersih):</span>
                                <strong className="text-emerald-700 font-bold">{formatDuration(group.fillingDuration)}</strong>
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">Durasi pengerjaan:</span>
                              <strong className="text-cyan-800 font-bold">{formatDuration(group.fillingDuration)}</strong>
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-0.5 font-bold">
                            <span className="text-slate-600">Output:</span>
                            <strong className="text-slate-900">{Math.round(group.fillingOutput || 0).toLocaleString('id-ID')} Pcs</strong>
                          </div>
                        </div>

                        {group.fillingNotes.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-cyan-200/50">
                            {group.fillingNotes.map((note, i) => <p key={i} className="text-[10px] text-cyan-800 italic leading-snug mb-1">"{note}"</p>)}
                          </div>
                        )}
                      </div>

                      {/* Packaging */}
                      <div className="bg-orange-50/50 rounded-xl p-3 border border-orange-100 relative space-y-2">
                        <div className="flex items-center gap-2 text-orange-700 font-bold text-sm">
                          <Package className="w-4 h-4" /> <span>Pengemasan</span>
                        </div>

                        <div className="text-[11px] text-slate-600 space-y-1 border-t border-orange-200/60 pt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Mulai:</span>
                            <strong className="text-slate-800 font-semibold">{formatFullDateTime(group.packingStartTime)}</strong>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-500">Selesai:</span>
                            <strong className="text-slate-800 font-semibold">{group.packingEndTime ? formatFullDateTime(group.packingEndTime) : 'Belum Selesai'}</strong>
                          </div>
                          {group.packingPause > 0 ? (
                            <>
                              <div className="flex items-center justify-between">
                                <span className="text-amber-700 font-semibold">Waktu Jeda:</span>
                                <strong className="text-amber-800 font-bold bg-amber-100/70 px-1.5 py-0.5 rounded text-[10px]">{formatDuration(group.packingPause)}</strong>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-slate-500">Waktu Asli (Bersih):</span>
                                <strong className="text-emerald-700 font-bold">{formatDuration(group.packingDuration)}</strong>
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span className="text-slate-500">Durasi pengerjaan:</span>
                              <strong className="text-orange-800 font-bold">{formatDuration(group.packingDuration)}</strong>
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-0.5 font-bold">
                            <span className="text-slate-600">Output:</span>
                            <strong className="text-slate-900">{Math.round(group.packingOutput || 0).toLocaleString('id-ID')} Pcs</strong>
                          </div>
                        </div>

                        {group.packingNotes.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-orange-200/50">
                            {group.packingNotes.map((note, i) => <p key={i} className="text-[10px] text-orange-800 italic leading-snug mb-1">"{note}"</p>)}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLogs.map(log => {
                const act = ACTIVITIES.find(a => a.value === log.activity) || ACTIVITIES[ACTIVITIES.length - 1];
                const ActIcon = act.icon;
                return (
                  <Card 
                    key={log.id} 
                    className={`group hover:shadow-md transition-shadow border-[#E6D5B8]/40 bg-white rounded-2xl ${
                      isSelectionMode && selectedIds.has(log.id) ? 'ring-2 ring-rose-500 bg-rose-50/20' : ''
                    }`}
                  >
                    <CardContent 
                      className={`p-4 flex items-center justify-between gap-4 transition-colors ${
                        isSelectionMode && !log.isLive ? 'cursor-pointer hover:bg-slate-50' : ''
                      }`}
                      onClick={() => isSelectionMode && !log.isLive && toggleSelection(log.id)}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        {/* Checkbox when in selection mode (non-live only) */}
                        {isSelectionMode && !log.isLive && (
                          <Checkbox 
                            checked={selectedIds.has(log.id)} 
                            onCheckedChange={() => toggleSelection(log.id)} 
                            className="shrink-0" 
                          />
                        )}
                        {/* Circular activity icon on the left */}
                        <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200/50 flex items-center justify-center text-[#8C6208] shrink-0">
                          <ActIcon className="w-5 h-5" />
                        </div>
                        
                        <div className="min-w-0 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-black text-sm text-[#302621]">{log.brand_name}</h3>
                            <span className="text-[10px] font-bold text-slate-400">·</span>
                            <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full capitalize font-bold">{log.shift}</span>
                            <ActivityBadge activity={log.activity} />
                            
                            {log.isLive && log.liveStatus === "in_progress" && (
                              <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold animate-pulse">
                                🔵 Berjalan
                              </span>
                            )}
                            {log.isLive && log.liveStatus === "on_hold" && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
                                🟠 Terjeda
                              </span>
                            )}
                            
                            {log.duration_minutes !== undefined && log.duration_minutes !== null && (
                              <span className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200/30 px-2 py-0.5 rounded-full font-bold">
                                Durasi: {formatDuration(log.duration_minutes)}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-3 text-xs text-[#7A695B] flex-wrap">
                            {log.isLive ? (
                              <span className="font-medium">Target: <strong className="text-blue-700 font-extrabold">{log.target_quantity?.toLocaleString()} pcs</strong></span>
                            ) : (
                              <span className="font-medium">Hasil: <strong className="text-emerald-700 font-extrabold">{log.qty_produced?.toLocaleString()} pcs</strong></span>
                            )}
                            {!log.isLive && log.waste_qty > 0 && (
                              <span className="font-medium">Waste: <strong className="text-red-600">{log.waste_qty?.toLocaleString()} pcs</strong></span>
                            )}
                            {log.target_po && (
                              <span className="text-[11px] bg-[#F9F5EC] text-[#8C6208] border border-[#E6D5B8]/30 px-1.5 py-0.5 rounded font-mono">
                                PO: {log.target_po}
                              </span>
                            )}
                            {log.operator && <span className="text-[11px] text-slate-400">oleh {log.operator}</span>}
                          </div>
                          
                          {log.notes && <p className="text-xs text-[#A39171] italic mt-1 leading-relaxed">Catatan: "{log.notes}"</p>}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {!log.isLive && !isSelectionMode && (
                          <>
                            <EntryDialog
                              entry={log}
                              orders={orders}
                              onSave={(data) => updateMutation.mutateAsync({ id: log.id, data })}
                              trigger={
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-[#8C6208] hover:bg-[#F9F5EC]">
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                              }
                            />
                            <Button
                              variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => { e.stopPropagation(); startSelection(log.id); }}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ==================== TAB 2: REKAP & ANALITIK ==================== */}
        <TabsContent value="rekap_analitik" className="space-y-6 outline-none">
          {/* Analytics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-[#E6D5B8]/50 shadow-xs bg-white rounded-2xl">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Progress Rata-rata</p>
                    <p className="text-2xl font-black mt-1.5">{avgProgress}%</p>
                    <p className="text-[11px] text-slate-400 font-medium mt-1">{donePO}/{totalPO} PO selesai</p>
                  </div>
                  <ProgressRing percentage={avgProgress} size={50} strokeWidth={4} />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-[#E6D5B8]/50 shadow-xs bg-white rounded-2xl">
              <CardContent className="p-5">
                <TrendingUp className="w-5 h-5 text-green-600 mb-2.5" />
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Produksi</p>
                <p className="text-2xl font-black mt-1.5">{totalQtyProduced.toLocaleString()}</p>
                <p className="text-[11px] text-slate-400 font-medium mt-1">{period} hari terakhir</p>
              </CardContent>
            </Card>

            <Card className="border-[#E6D5B8]/50 shadow-xs bg-white rounded-2xl">
              <CardContent className="p-5">
                <AlertTriangle className={`w-5 h-5 mb-2.5 ${Number(wasteRate) > 5 ? 'text-red-500' : 'text-yellow-500'}`} />
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Waste Rate</p>
                <p className="text-2xl font-black mt-1.5">{wasteRate}%</p>
                <p className="text-[11px] text-slate-400 font-medium mt-1">{totalWasteProduced.toLocaleString()} pcs waste</p>
              </CardContent>
            </Card>

            <Card className="border-[#E6D5B8]/50 shadow-xs bg-white rounded-2xl">
              <CardContent className="p-5">
                <Package className={`w-5 h-5 mb-2.5 ${criticalItems > 0 ? 'text-red-500' : 'text-green-600'}`} />
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Stok Kritis</p>
                <p className="text-2xl font-black mt-1.5">{criticalItems}</p>
                <p className="text-[11px] text-slate-400 font-medium mt-1">item habis/minus</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chart 1: BarChart Output Harian */}
            <Card className="border-[#E6D5B8]/50 shadow-xs lg:col-span-2 bg-white rounded-2xl">
              <CardHeader><CardTitle className="text-base font-bold">Produksi & Waste (14 Hari Terakhir)</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={last14} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fontWeight: 'bold' }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="qty" name="Produksi" fill="#8C6208" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="waste" name="Waste" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Chart 2: Distribusi Aktivitas */}
            <Card className="border-[#E6D5B8]/50 shadow-xs bg-white rounded-2xl">
              <CardHeader><CardTitle className="text-base font-bold">Porsi Aktivitas</CardTitle></CardHeader>
              <CardContent>
                {activityData.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-16 font-medium">Belum ada data produksi</p>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={activityData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {activityData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Kesiapan Komponen */}
            <Card className="border-[#E6D5B8]/50 shadow-xs bg-white rounded-2xl">
              <CardHeader><CardTitle className="text-base font-bold">Kesiapan Komponen per Jenis</CardTitle></CardHeader>
              <CardContent>
                {compData.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-12 font-medium">Tidak ada data PO komponen</p>
                ) : (
                  <div className="space-y-4">
                    {compData.map(comp => (
                      <div key={comp.name} className="flex items-center gap-3">
                        <div className="w-24 text-xs font-bold truncate text-[#302621]">{comp.name}</div>
                        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                          <div
                            className={`h-full rounded-full transition-all ${comp.pct >= 100 ? 'bg-emerald-500' : comp.pct >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                            style={{ width: `${comp.pct}%` }}
                          />
                        </div>
                        <div className="text-[10px] text-slate-400 font-mono w-16 text-right font-bold">{comp.pct}%</div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quality Control Scores per Brand */}
            <Card className="border-[#E6D5B8]/50 shadow-xs bg-white rounded-2xl">
              <CardHeader><CardTitle className="text-base font-bold">Rangkuman Kualitas QC per Mitra</CardTitle></CardHeader>
              <CardContent>
                {qcData.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-12 font-medium">Belum ada uji sampel QC</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 text-slate-400 text-xs font-bold pb-2 text-center">
                          <th className="py-2 text-left">Brand Mitra</th>
                          <th className="py-2 text-center">Avg Skor</th>
                          <th className="py-2 text-right">Tingkat Kelulusan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {qcData.map(row => (
                          <tr key={row.brand} className="hover:bg-slate-50/50">
                            <td className="py-3 font-bold text-xs">{row.brand}</td>
                            <td className="py-3 text-center">
                              <span className={`text-xs font-black ${Number(row.avgScore) >= 8 ? 'text-emerald-600' : Number(row.avgScore) >= 6 ? 'text-amber-600' : 'text-red-500'}`}>
                                {row.avgScore}/10
                              </span>
                            </td>
                            <td className="py-3 text-right">
                              <span className="text-xs font-black text-[#302621]">{row.accRate}% Keluar</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* MODAL CHECKLIST QC STATUS */}
      {qcModalData && (
        <Dialog open={!!qcModalData} onOpenChange={() => setQcModalData(null)}>
          <DialogContent className="max-w-md w-[95vw] bg-[#FFFDF9] border-[#E6D5B8] p-5">
            <DialogHeader className="pb-2 border-b border-[#E6D5B8]/40">
              <DialogTitle className="text-base font-black text-[#302621] flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#8C6208]" />
                Hasil Audit & Checklist QC
              </DialogTitle>
              <p className="text-xs text-[#7A695B] mt-0.5">
                Brand: <strong>{qcModalData.brand_name || 'Tanpa Brand'}</strong> {qcModalData.target_po ? `· PO: ${qcModalData.target_po}` : ''}
              </p>
            </DialogHeader>

            <div className="space-y-4 pt-2">
              {/* QC Inspector Info */}
              <div className="bg-[#F9F5EC] p-3 rounded-xl border border-[#E6D5B8]/60 flex items-center justify-between text-xs">
                <div>
                  <span className="text-[#A39171] font-bold block text-[10px] uppercase">Pemeriksa QC</span>
                  <span className="font-extrabold text-[#302621]">{qcModalData.qcData?.dikonfirmasi_oleh || 'Tim Quality Control'}</span>
                </div>
                <div className="text-right">
                  <span className="text-[#A39171] font-bold block text-[10px] uppercase">Waktu Inspeksi</span>
                  <span className="font-semibold text-slate-700">{formatFullDateTime(qcModalData.qcData?.dikonfirmasi_pada || new Date())}</span>
                </div>
              </div>

              {/* 4 Items Checklist Status */}
              <div className="space-y-2">
                <p className="text-[10px] font-black uppercase text-[#A39171] tracking-wider">Item Audit Checklist:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className={`p-2.5 rounded-xl border flex items-center gap-2 ${qcModalData.qcData?.checklist?.fisik !== false ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span className="font-bold">1. Fisik Botol/Kemasan</span>
                  </div>
                  <div className={`p-2.5 rounded-xl border flex items-center gap-2 ${qcModalData.qcData?.checklist?.volume !== false ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span className="font-bold">2. Uji Netto Volume</span>
                  </div>
                  <div className={`p-2.5 rounded-xl border flex items-center gap-2 ${qcModalData.qcData?.checklist?.label !== false ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span className="font-bold">3. Cetak Label Produk</span>
                  </div>
                  <div className={`p-2.5 rounded-xl border flex items-center gap-2 ${qcModalData.qcData?.checklist?.segel !== false ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span className="font-bold">4. Segel & Packaging</span>
                  </div>
                </div>
              </div>

              {/* Qty Audit Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50/80 border border-emerald-200 p-3 rounded-xl text-center">
                  <span className="text-[10px] uppercase font-bold text-emerald-700">Lolos QC</span>
                  <p className="text-lg font-black text-emerald-800 mt-0.5">
                    {(qcModalData.qcData?.qty_lolos ?? qcModalData.qcData?.qty_target ?? 0).toLocaleString('id-ID')} Pcs
                  </p>
                </div>
                <div className="bg-rose-50/80 border border-rose-200 p-3 rounded-xl text-center">
                  <span className="text-[10px] uppercase font-bold text-rose-700">Reject / Defect</span>
                  <p className="text-lg font-black text-rose-800 mt-0.5">
                    {(qcModalData.qcData?.qty_reject || 0).toLocaleString('id-ID')} Pcs
                  </p>
                </div>
              </div>

              {qcModalData.qcData?.catatan_reject && (
                <div className="bg-rose-50 border border-rose-200 p-3 rounded-xl text-xs space-y-1">
                  <span className="font-bold text-rose-800 block">Catatan Reject / Keterangan:</span>
                  <p className="text-rose-700 italic">{qcModalData.qcData.catatan_reject}</p>
                </div>
              )}
            </div>

            <div className="pt-2 flex justify-end">
              <Button className="bg-[#302621] text-white hover:bg-[#4A3B32] text-xs h-8 px-4" onClick={() => setQcModalData(null)}>
                Tutup
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}